// Live Wildix calls on the Prospection screen (JBWEB-1400): what an update of
// the socket event 'crmPhoneCall' (or of phone/live) does to the screen.

// French numbers have 9 significant digits: comparing the last 9 matches
// « 0696 12 34 56 » with the « +596696123456 » Wildix sends.
const SIGNIFICANT_DIGITS = 9;

export const phoneKey = (raw) => {
  const digits = String(raw ?? '').replace(/\D/g, '');
  return digits.length >= SIGNIFICANT_DIGITS
    ? digits.slice(-SIGNIFICANT_DIGITS)
    : null;
};

// The numbers of a sheet: the one it dials and those of its members and contacts.
const sheetPhoneKeys = (company) =>
  [company?.phone, ...(company?.members ?? []).map((member) => member?.phone)]
    .map(phoneKey)
    .filter(Boolean);

const isSheetCall = (call, sheet) => {
  if (call.companyId && call.companyId === sheet.company?.id) return true;
  const key = phoneKey(call.remotePhone);
  return Boolean(key) && sheetPhoneKeys(sheet.company).includes(key);
};

/**
 * What a live call update does, the 5 rules of JBWEB-1400 in order.
 * - call: the LiveCall received;
 * - current: the last LiveCall the screen followed, or null;
 * - sheet: the open sheet { company, callId } (callId: the live call shown on
 *   it, or null), or null.
 * Returns { action: 'stay' } (the sheet shows the call), { action: 'open',
 * companyId }, { action: 'toast', number } (number null when masked) or
 * { action: 'ignore' }.
 */
export const decideLiveCall = ({ call, current = null, sheet = null }) => {
  if (!call?.id) return { action: 'ignore' };
  // The call already on the sheet: its new state, nothing replaced.
  if (sheet && sheet.callId === call.id) return { action: 'stay' };
  const completed = call.status === 'completed';
  // 1. A completed call updated afterwards does not replace the current call.
  if (
    completed &&
    current &&
    (current.id !== call.id || current.status === 'completed')
  ) {
    return { action: 'ignore' };
  }
  // 2. Several sheets can share a number: a call to the open one's stays on it.
  if (sheet && isSheetCall(call, sheet)) return { action: 'stay' };
  // 3. A completed call does not replace an open sheet.
  if (completed && sheet) return { action: 'ignore' };
  // 4. An unknown number is told at ringing only.
  if (!call.companyId) {
    return call.status === 'ringing'
      ? { action: 'toast', number: call.remotePhone || null }
      : { action: 'ignore' };
  }
  // 5. The prospect's sheet opens.
  return { action: 'open', companyId: call.companyId };
};

/**
 * The call the screen follows after an update: the update itself when it was
 * acted on, the same call's new state, or the one followed before.
 */
export const nextCurrentCall = (current, call, decision) => {
  if (decision.action !== 'ignore') return call;
  if (current && call?.id === current.id) return call;
  return current;
};

const pad = (value) => String(value).padStart(2, '0');

// « 1:42 », the time on the line.
export const talkClock = (seconds) => {
  const total = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(total / 60)}:${pad(total % 60)}`;
};

// Seconds since talkingAt at `now`, else the talk time the api counted.
export const talkingSeconds = (call, now) => {
  const since = call?.talkingAt ? Date.parse(call.talkingAt) : NaN;
  if (Number.isFinite(since)) {
    return Math.max(0, Math.floor((now.getTime() - since) / 1000));
  }
  return Math.max(0, Math.floor(call?.talkSeconds || 0));
};

// « 45 s » under a minute, else « 1 min 05 »: the texts come from the locales.
export const talkDuration = (seconds, text) => {
  const total = Math.max(0, Math.floor(seconds || 0));
  if (total < 60) return text('sheet.status.seconds', { seconds: total });
  return text('sheet.status.minutes', {
    minutes: Math.floor(total / 60),
    seconds: pad(total % 60),
  });
};

// Under this talk time an answered call is most likely the answering machine.
const MIN_TALK_SECONDS = 10;

/**
 * What the sheet proposes when its call hangs up: { result, line, seconds },
 * line naming the Wildix sentence (qualification.proposal.<line>); null while
 * the call is not completed.
 */
export const hangUpProposal = (call) => {
  if (call?.status !== 'completed') return null;
  const seconds = Math.max(0, Math.floor(call.talkSeconds || 0));
  if (!call.answered) {
    return { result: 'no_answer', line: 'notAnswered', seconds };
  }
  if (seconds >= MIN_TALK_SECONDS) {
    return { result: 'answered', line: 'answered', seconds };
  }
  return { result: 'no_answer', line: 'machine', seconds };
};
